import dayjs from 'utils/dayjs';
import {spotheroGet} from 'api';

import {displayDateFormat} from 'utils/date-time';
import {sendSentryException} from 'utils/error-handling';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';
import {type InventoryRule} from 'api/inventory/inventory.types';
import {type RawApiResponse} from 'api/api.types';

interface GetRuleDisplayParams {
    id: number;
}

type GetRuleDisplayRawResponse = RawApiResponse<InventoryRule>;

interface GetRuleDisplayRequest {
    rangeStarts: string;
    rangeEnds: string;
    excludeEventRules: boolean;
    excludeEventFilterRules: boolean;
    excludeExceptionRules: boolean;
}

type GetRuleDisplayRawRequest = RawApiResponse<GetRuleDisplayRequest>;

/**
 * Formats the request object for getting a detailed list of inventory from the start of the current hour. Deleted rules are excluded.
 * @param request
 */
const formatGetRuleDisplayRequest = (
    request: Partial<GetRuleDisplayRequest> | null = {}
): Partial<GetRuleDisplayRawRequest> => {
    const data = {
        ...request,
        rangeStarts: dayjs()
            .startOf('hour')
            .format(`${displayDateFormat}T00:00`),
    };

    const formattedRequest = convertKeysToSnakeCase(data);

    return formattedRequest;
};

/**
 * Formats the response object for getting a detailed list of inventory from the start of the current hour. Deleted rules are excluded.
 * @param response
 */
const formatGetRuleDisplayResponse = (
    response: GetRuleDisplayRawResponse[]
): InventoryRule[] => {
    const formattedResponse = camelizeKeys(response);

    return formattedResponse;
};

/**
 * Gets a detailed list of inventory from the start of the current hour. Deleted rules are excluded.
 *
 * @param id - The facility id.
 *
 * @see https://spothero.com/api/v1/docs/endpoints/?new-docs=true#!/Facilities/get_facilities_facility_id_inventory_rules_range
 * @see '/api/v1/facilities/{id}/inventory-rules-range/'
 */
const getRuleDisplay = async ({id}: GetRuleDisplayParams) => {
    try {
        const formattedRequest = formatGetRuleDisplayRequest();

        const response = await spotheroGet<GetRuleDisplayRawResponse[]>(
            `facilities/${id}/inventory-rules-range/`,
            formattedRequest
        );

        const formattedResponse = formatGetRuleDisplayResponse(
            response.data.data.results
        );

        return formattedResponse;
    } catch (err) {
        sendSentryException(err);

        throw new Error(err.message);
    }
};

export default getRuleDisplay;
